import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  PlayCircleIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
  BookOpenIcon,
  CogIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import HelpArticle from '../components/HelpArticle';
import { getArticleById, searchArticles } from '../data/helpArticles';

const HelpCenterPage = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  const categories = [
    {
      icon: <BookOpenIcon className="h-8 w-8" />,
      title: "Getting Started",
      description: "Learn the basics of FBRSTAPP",
      articles: [
        { id: "how-to-create-your-first-invoice", title: "How to create your first invoice" },
        { id: "setting-up-your-fbr-credentials", title: "Setting up your FBR credentials" },
        { id: "understanding-the-dashboard", title: "Understanding the dashboard" },
        { id: "connecting-to-fbr-api", title: "Connecting to FBR API" }
      ]
    },
    {
      icon: <DocumentTextIcon className="h-8 w-8" />,
      title: "Invoice Management",
      description: "Everything about invoices",
      articles: [
        { id: "creating-and-editing-invoices", title: "Creating and editing invoices" },
        { id: "submitting-to-fbr", title: "Submitting to FBR" },
        { id: "handling-rejections", title: "Handling rejections" },
        { id: "bulk-operations", title: "Bulk operations" }
      ]
    },
    {
      icon: <CogIcon className="h-8 w-8" />,
      title: "Account Management",
      description: "Manage your account settings",
      articles: [
        { id: "updating-profile", title: "Updating your profile" },
        { id: "managing-team", title: "Managing team members" },
        { id: "billing-subscriptions", title: "Billing and subscriptions" },
        { id: "security-settings", title: "Security settings" }
      ]
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "Compliance & Security",
      description: "FBR compliance and security",
      articles: [
        { id: "fbr-compliance", title: "FBR compliance requirements" },
        { id: "data-security", title: "Data security measures" },
        { id: "audit-trails", title: "Audit trails" },
        { id: "backup-recovery", title: "Backup and recovery" }
      ]
    }
  ];

  const popularArticles = [
    { id: "how-to-create-your-first-invoice", title: "How to submit your first invoice to FBR" },
    { id: "connecting-to-fbr-api", title: "Troubleshooting FBR API connection issues" },
    { id: "handling-rejections", title: "Understanding FBR rejection codes" },
    { id: "creating-and-editing-invoices", title: "Setting up QR code printing" },
    { id: "understanding-the-dashboard", title: "Managing multiple business locations" },
    { id: "submitting-to-fbr", title: "Bulk invoice submission guide" }
  ];

  const videoTutorials = [
    {
      title: "FBRSTAPP Overview - Getting Started",
      duration: "5:30",
      thumbnail: "/api/placeholder/300/200"
    },
    {
      title: "Creating Your First Invoice",
      duration: "8:15",
      thumbnail: "/api/placeholder/300/200"
    },
    {
      title: "FBR API Integration Setup",
      duration: "12:45",
      thumbnail: "/api/placeholder/300/200"
    }
  ];

  // Handle article selection
  const handleArticleClick = (articleId) => {
    const article = getArticleById(articleId);
    if (article) {
      setSelectedArticle(article);
    }
  };

  // Handle search
  const handleSearch = (query) => {
    if (query.trim()) {
      const results = searchArticles(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  // Handle back to help center
  const handleBackToHelpCenter = () => {
    setSelectedArticle(null);
    setSearchResults([]);
    setSearchQuery('');
  };

  // If an article is selected, show the article
  if (selectedArticle) {
    return <HelpArticle article={selectedArticle} onBack={handleBackToHelpCenter} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/home" className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="text-sm text-gray-600">Help Center</p>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              {user ? (
                <Link to="/app/dashboard" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium">Sign In</Link>
                  <Link to="/register" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Get Started</Link>
                </>
              )}
              <Link
                to="/home"
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Search */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">How can we help you?</h1>
          <p className="text-xl text-blue-100 mb-8">
            Search our knowledge base for answers to your questions
          </p>
          
          <div className="relative max-w-2xl mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              placeholder="Search for help articles..."
              className="w-full px-6 py-4 pl-12 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <MagnifyingGlassIcon className="absolute left-4 top-4 h-6 w-6 text-gray-400" />
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="max-w-2xl mx-auto mt-6">
              <div className="bg-white rounded-lg shadow-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Search Results ({searchResults.length})
                </h3>
                <div className="space-y-2">
                  {searchResults.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => handleArticleClick(article.id)}
                      className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <h4 className="font-medium text-gray-900">{article.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{article.description}</p>
                      <span className="text-xs text-blue-600 mt-1 inline-block">{article.category}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Categories */}
      {searchResults.length === 0 && (
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Browse by Category
            </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border hover:shadow-xl transition-shadow">
                <div className="text-blue-600 mb-4">
                  {category.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {category.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {category.description}
                </p>
                <ul className="space-y-2">
                  {category.articles.map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <button
                        onClick={() => handleArticleClick(article.id)}
                        className="flex items-center text-gray-700 hover:text-blue-600 cursor-pointer transition-colors w-full text-left"
                      >
                        <ChevronRightIcon className="h-4 w-4 mr-2" />
                        <span className="text-sm">{article.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Popular Articles */}
      {searchResults.length === 0 && (
        <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Popular Articles
          </h2>
          
          <div className="space-y-4">
            {popularArticles.map((article, index) => (
              <button 
                key={index}
                onClick={() => handleArticleClick(article.id)}
                className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors w-full text-left"
              >
                <DocumentTextIcon className="h-5 w-5 text-blue-600 mr-3" />
                <span className="text-gray-900 hover:text-blue-600">{article.title}</span>
                <ChevronRightIcon className="h-4 w-4 text-gray-400 ml-auto" />
              </button>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Video Tutorials */}
      {searchResults.length === 0 && (
        <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Video Tutorials
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {videoTutorials.map((video, index) => (
              <a 
                key={index}
                href={`https://wa.me/923080323323?text=Hi%2C%20I%20would%20like%20to%20watch%20the%20video%20tutorial%3A%20${encodeURIComponent(video.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
              >
                <div className="relative">
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <PlayCircleIcon className="h-16 w-16 text-blue-600" />
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                    {video.duration}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{video.title}</h3>
                  <p className="text-sm text-gray-600">Click to request this tutorial via WhatsApp</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Still Need Help */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <QuestionMarkCircleIcon className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">Still need help?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/support"
              className="px-8 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-100 font-medium"
            >
              Contact Support
            </Link>
            <Link
              to="/community"
              className="px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Join Community
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenterPage;